import { Button } from '@/components/ui/button';
import { AlertCircle, FileUp, Loader2 } from 'lucide-react';
import { useGetList, useNotify, useRecordContext } from 'ra-core';
import { useNavigate } from 'react-router-dom';
import type { BoM } from '../types';

export const CreateQuotationFromBoMButton = () => {
    const record   = useRecordContext<BoM>();
    const navigate = useNavigate();
    const notify   = useNotify();

    // Only show for approved BoMs
    if (!record || record.status !== 'approved') return null;

    return (
        <CreateQuotationInner record={record} navigate={navigate} notify={notify} />
    );
};

// ── Inner component — hooks must be called unconditionally ────────────────────
const CreateQuotationInner = ({
    record,
    navigate,
    notify,
}: {
    record: BoM;
    navigate: ReturnType<typeof useNavigate>;
    notify: ReturnType<typeof useNotify>;
}) => {
    // ── Duplicate guard: check if quotation for this BoM already exists ───────
    const bomQuotationRef = `Q-from-BOM-${record.reference}`;

    const { data: existingQuotations, isLoading: checking } = useGetList(
        'quotations',
        {
            filter: { reference: bomQuotationRef },
            pagination: { page: 1, perPage: 1 },
        }
    );

    const duplicateExists = existingQuotations && existingQuotations.length > 0;

    // ── Blocked state ─────────────────────────────────────────────────────────
    if (duplicateExists) {
        return (
            <Button
                disabled
                variant="outline"
                className="border-orange-300 text-orange-600 bg-orange-50 cursor-not-allowed"
                title={`Quotation already exists: ${bomQuotationRef}`}
            >
                <AlertCircle className="w-4 h-4 mr-2" />
                Quotation Exists
            </Button>
        );
    }

    // ── Loading state while checking ─────────────────────────────────────────
    if (checking) {
        return (
            <Button disabled className="bg-blue-600 text-white opacity-70">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Checking…
            </Button>
        );
    }

    const handleCreateQuotation = () => {
        if (!record.items || record.items.length === 0) {
            notify('Cannot create quotation: BoM has no items.', { type: 'warning' });
            return;
        }

        // ── Direct 1-to-1 field copy ──────────────────────────────────────────
        // BoMItem fields now match DocumentItemsTable fields exactly:
        //   description | hsn_code | quantity | unit | estimated_unit_cost → unit_price
        const qItems = record.items.map(item => ({
            description:    item.description,
            hsn_code:       item.hsn_code || '',
            quantity:       item.quantity,
            unit:           item.unit,
            unit_price:     item.estimated_unit_cost,  // BOM name → Quotation name
            tax_rate:       0,    // user fills GST% in the quotation form
            tax_amount:     0,    // GrandTotalsWatcher auto-recalculates
            line_total:     item.quantity * item.estimated_unit_cost,
        }));

        navigate('/quotations/create', {
            state: {
                record: {
                    project_id: record.project_id,
                    reference:  bomQuotationRef,
                    items:      qItems,
                }
            }
        });
    };

    return (
        <Button
            onClick={handleCreateQuotation}
            className="bg-blue-600 hover:bg-blue-700 text-white"
        >
            <FileUp className="w-4 h-4 mr-2" />
            Create Quotation
        </Button>
    );
};
